import { NOTICE_PUBLIC_PATH } from './noticeRoutes'
import { images } from './images'

/** 기관 유형 2종 */
export const ORG_TYPE_FACILITY = '요양원,주간보호센터'
export const ORG_TYPE_VISIT = '방문요양(목욕)'

export interface PortalShortcut {
  label: string
  icon: string
  to: string
  desc?: string
  /** 즐겨찾기 고유 키 (경로가 같아도 항목을 구분). 미지정 시 to 사용 */
  key?: string
}

export interface PortalNotice {
  id: string
  category: string
  title: string
  date: string
  isNew?: boolean
  to: string
}

export interface PortalQuickMenu {
  label: string
  icon: string
  to: string
}

export interface PortalFaq {
  q: string
  a: string
  category?: string
}

export interface PortalBanner {
  id: string
  tag: string
  title: string
  desc: string
  metric: string
  cta: string
  to: string
  tone: 'wage' | 'accounting' | 'ai'
  /** 카드 상단 대표 이미지 (public/images) */
  image?: string
  imageAlt?: string
}

export const PORTAL_SHORTCUTS: PortalShortcut[] = [
  { label: '요양시설 임금관리', icon: 'mdi-home-heart', to: '/facility', desc: '인건비 · 통상임금' },
  { label: '방문센터 임금관리', icon: 'mdi-map-marker-radius', to: '/visiting-care', desc: '청구 · 급여 연계' },
  { label: '함우리몰', icon: 'mdi-cart-percent', to: '/mall', desc: '기관 공동구매' },
  { label: '임금관리', icon: 'mdi-cash-multiple', to: '/wage', desc: '설계 · 대장 · 신고' },
  { label: '회계관리', icon: 'mdi-calculator-variant-outline', to: '/accounting', desc: '장부 · 예결산' },
  { label: 'AI 서비스', icon: 'mdi-robot-outline', to: NOTICE_PUBLIC_PATH, desc: '고시 · 업무 지원' },
  { label: '상담 안내', icon: 'mdi-headset', to: '/support', desc: '기관 운영 상담' },
  { label: '공지사항', icon: 'mdi-bullhorn-outline', to: NOTICE_PUBLIC_PATH, desc: '운영 · 고시 안내' },
]

export const PORTAL_NOTICES: PortalNotice[] = [
  {
    id: 'n1',
    category: '공통',
    title: '2025년 장기요양급여 고시 개정 안내 및 대응 가이드 배포',
    date: '2026.06.28',
    isNew: true,
    to: NOTICE_PUBLIC_PATH,
  },
  {
    id: 'n2',
    category: '임금',
    title: '통상임금 판결 이후 임금 구조 점검 무료 진단 접수 안내',
    date: '2026.06.25',
    isNew: true,
    to: '/wage',
  },
  {
    id: 'n3',
    category: '쇼핑몰',
    title: '공동구매 7월 위생용품·식품 카탈로그 업데이트',
    date: '2026.06.20',
    to: '/mall',
  },
  {
    id: 'n4',
    category: '공통',
    title: '회원가입 안내',
    date: '2026.06.15',
    to: '/signup',
  },
  {
    id: 'n5',
    category: '공통',
    title: '상담 운영시간 변경 안내',
    date: '2026.06.10',
    to: '/support',
  },
]

export const PORTAL_QUICK_MENUS: PortalQuickMenu[] = [
  { label: '회원가입', icon: 'mdi-account-plus-outline', to: '/signup' },
  { label: '로그인', icon: 'mdi-login', to: '/login' },
  { label: '무료 임금 진단', icon: 'mdi-clipboard-check-outline', to: '/consult-board/write' },
  { label: '고시 검색', icon: 'mdi-file-search-outline', to: NOTICE_PUBLIC_PATH },
  { label: '함우리몰', icon: 'mdi-cart-outline', to: '/mall' },
  { label: '임금 서비스', icon: 'mdi-cash', to: '/wage' },
  { label: '회계 서비스', icon: 'mdi-book-open-variant', to: '/accounting' },
  { label: '방문센터 임금관리', icon: 'mdi-home-account', to: '/visiting-care' },
  { label: '마이페이지', icon: 'mdi-account-circle-outline', to: '/my-info' },
]

/** 메인 — 기관유형별 임금 진입 카드 */
export const PORTAL_WAGE_INTRO: PortalBanner[] = [
  {
    id: 'visit-wage',
    tag: ORG_TYPE_VISIT,
    title: '업계 최저가로 편리한 급여 업무',
    desc: '종사자 수에 따라 실질 부담을 바로 계산합니다. 임금대장 절감까지 반영한 예시입니다.',
    metric: '월 3만원부터',
    cta: '실질 부담 계산하기',
    to: '/visiting-care',
    tone: 'wage',
  },
  {
    id: 'facility-wage',
    tag: ORG_TYPE_FACILITY,
    title: '통상임금 리스크를 숫자로 확인',
    desc: '근로자 수 기준 3년 통상임금 리스크와 절감 가능액 예시입니다.',
    metric: '리스크 6,683만원 → 절감 3,348만원',
    cta: '리스크 계산하기',
    to: '/facility',
    tone: 'wage',
  },
]

export const PORTAL_BANNERS: PortalBanner[] = [
  {
    id: 'wage',
    tag: '임금관리',
    title: '인건비 절감과 통상임금 리스크를 한 번에',
    desc: '수당 · 비과세 설계부터 임금대장 · 4대보험 · 세무 신고까지. 장기요양기관에 맞춘 임금 구조를 설계합니다.',
    metric: '평균 인건비 7% 절감',
    cta: '무료 진단 받기',
    to: '/consult-board/write',
    tone: 'wage',
    image: images.wage,
    imageAlt: '급여 자료를 검토하는 모습',
  },
  {
    id: 'accounting',
    tag: '회계관리',
    title: '장기요양기관 회계규칙에 맞는 장부 · 예결산',
    desc: '임금 설계와 연계된 회계 흐름으로 감사 · 점검 대비와 경영 가시성을 확보합니다.',
    metric: '결산 준비시간 60% 단축',
    cta: '회계 서비스 보기',
    to: '/accounting',
    tone: 'accounting',
    image: images.accounting,
    imageAlt: '계산기와 재무 서류',
  },
  {
    id: 'ai',
    tag: 'AI 업무지원',
    title: '복잡한 고시 해석과 행정 업무를 AI가 빠르게',
    desc: '복잡한 장기요양 고시와 개정사항을 현장 언어로 풀어드립니다. 반복 행정을 줄이고 돌봄에 집중하세요.',
    metric: '문서 작성시간 4시간 절약',
    cta: 'AI 서비스 문의',
    to: '/consult-board/write',
    tone: 'ai',
    image: images.law,
    imageAlt: '법령·고시 자료',
  },
]

export const PORTAL_FAQS: PortalFaq[] = [
  {
    q: '장기요양지원센터는 어떤 기관을 위한 서비스인가요?',
    a: '요양원·주간보호센터·방문요양센터의 급여, 노무, 회계 업무를 지원합니다. 직원 급여부터 4대보험·원천세, 임금대장과 회계까지 기관 운영 실무를 하나의 흐름으로 연결합니다.',
    category: '공통',
  },
  {
    q: '회원가입 없이도 이용할 수 있나요?',
    a: '공지사항과 고시 검색은 비회원도 이용할 수 있습니다. 함우리몰은 로그인한 회원만 볼 수 있습니다. 임금 · 회계 등 기관 맞춤 서비스도 회원가입 후 이용하실 수 있습니다.',
    category: '공통',
  },
  {
    q: '카이온(임금 · 회계)을 쓰지 않아도 함우리몰(공동구매)에 참여할 수 있나요?',
    a: '네. 카이온 이용 여부와 관계없이 함우리몰 공동구매에 참여하실 수 있습니다. 카이온을 이용 중인 시설은 추가 할인 혜택이 적용됩니다.',
    category: '쇼핑몰',
  },
  {
    q: '임금 구조 점검은 어떻게 신청하나요?',
    a: '상담 신청에 글을 남기시면 담당 전문가가 확인 후 연락드립니다. 최근 1개월분 임금대장만 있으면 점검을 시작할 수 있습니다.',
    category: '임금',
  },
  {
    q: '상담 운영시간은 어떻게 되나요?',
    a: '평일 09:30 – 17:30 운영합니다. 상담 신청을 남겨 주시면 담당자가 확인 후 답변드립니다.',
    category: '공통',
  },
]

export const PORTAL_ALL_MENU = [
  {
    title: '소식',
    items: [
      { label: '공지사항', to: '/announcements' },
      { label: '자주 묻는 질문', to: '/faq' },
      { label: '통합 검색', to: '/search' },
    ],
  },
  {
    title: '상담',
    items: [
      { label: '상담 안내', to: '/support' },
      { label: '상담 신청', to: '/consult-board/write' },
      { label: '상담 신청 내역', to: '/consult-board' },
      { label: '찾아오시는 길', to: '/support' },
    ],
  },
]

export interface PortalMainNav {
  label: string
  to: string
  children?: { label: string; desc?: string; to: string }[]
}

export interface PortalOrgGuide {
  id: string
  label: string
  icon: string
  desc: string
  links: { label: string; to: string }[]
}

export interface PortalOneStopTab {
  id: string
  label: string
  links: { label: string; to: string; icon: string }[]
}

export interface PortalBenefit {
  title: string
  desc: string
  to: string
}

export interface PortalPromoSlide {
  title: string
  desc: string
  cta: string
  to: string
  tone: 'wage' | 'accounting' | 'buy'
}

export interface PortalAuthLink {
  label: string
  icon: string
  to: string
}

export const ORG_LABEL: Record<'facility' | 'visit', string> = {
  facility: '요양원·주간보호',
  visit: '방문요양·방문목욕',
}

/** caion_crawl_biz_map.cbm_short — 포털 2종에 묶은 공식 사업구분 */
export const ORG_BIZ_LABEL: Record<'facility' | 'visit', string> = {
  facility: '요양시설·주야간보호',
  visit: '방문요양·방문목욕',
}

export const ORG_KIND: Record<'facility' | 'visit', string> = {
  facility: '시설형 장기요양기관',
  visit: '재가형 장기요양기관',
}

export interface PortalCorpNavChild {
  label: string
  to: string
  /** 임금관리 안의 기관 유형처럼 한 단계 더 들어간 항목 */
  nested?: boolean
  /** true면 링크로 연결하지 않고 그룹 제목처럼만 보인다 (하위 메뉴로만 진입) */
  disabled?: boolean
  /** 관리자(슈퍼바이저) 세션에서만 메뉴에 보인다 */
  adminOnly?: boolean
}

export interface PortalCorpNavItem {
  label: string
  to: string
  match?: string[]
  children?: PortalCorpNavChild[]
}

/** 원페이지 홈의 스크롤 구역. 상단·푸터 메뉴가 이 순서로 내려가고, 현재 위치에 맞춰 켜진다. */
export const PORTAL_HOME_SECTIONS = [
  { id: 'brand', label: '소개' },
  { id: 'values', label: '하는 일' },
  { id: 'why', label: '강점' },
  { id: 'process', label: '이용 안내' },
] as const

/** 상단 대메뉴. 쇼핑몰은 우측 함우리몰 링크다. */
export const PORTAL_CORP_NAV: PortalCorpNavItem[] = [
  {
    label: '서비스',
    to: '/wage',
    match: ['/wage', '/facility', '/visiting-care', '/accounting', '/services'],
    children: [
      /* 임금관리는 사업구분 하위 메뉴로만 들어간다 */
      { label: '임금관리', to: '/wage', disabled: true },
      { label: '요양원·주간보호', to: '/facility', nested: true },
      { label: '방문요양·목욕', to: '/visiting-care', nested: true },
      { label: '재무회계', to: '/accounting' },
    ],
  },
  {
    label: '상담',
    to: '/support',
    match: ['/support', '/consult-board'],
    children: [
      { label: '상담 안내', to: '/support' },
      { label: '상담 신청', to: '/consult-board/write' },
    ],
  },
]

/** 정부24형 GNB (Mega Menu) */
export const PORTAL_MAIN_NAV: PortalMainNav[] = [
  { label: '소식', to: '/announcements' },
  {
    label: '상담',
    to: '/support',
    children: [
      { label: '상담 신청', desc: '기관 운영 상담', to: '/consult-board/write' },
    ],
  },
]

/** 메가메뉴 (서비스 허브형) */
export interface MegaLink {
  label: string
  to: string
  desc?: string
}

export interface MegaColumn {
  title?: string
  icon?: string
  links: MegaLink[]
}

export interface MegaFeature {
  badge: string
  title: string
  desc: string
  cta: string
  to: string
  icon: string
}

export interface PortalMegaNav {
  label: string
  to: string
  /** 메가메뉴 좌측 대표 페이지 바로가기 라벨 (미지정 시 '전체보기') */
  allLabel?: string
  columns: MegaColumn[]
  feature: MegaFeature
}

export const PORTAL_MEGA_NAV: PortalMegaNav[] = [
  {
    label: '소식',
    to: '/announcements',
    allLabel: '공지사항 전체보기',
    columns: [
      {
        title: '소식 · 자료',
        icon: 'mdi-bullhorn-outline',
        links: [
          { label: '공지사항', to: '/announcements', desc: '운영 · 제도 안내' },
          { label: '자주 묻는 질문', to: '/faq', desc: 'FAQ' },
          { label: '통합 검색', to: '/search', desc: '사이트 전체 검색' },
        ],
      },
    ],
    feature: {
      badge: 'NEW',
      title: '2025 고시 개정 안내',
      desc: '급여 산정 · 청구 변경사항을 확인하세요.',
      cta: '확인하기',
      to: '/announcements',
      icon: 'mdi-bullhorn-outline',
    },
  },
  {
    label: '상담',
    to: '/support',
    allLabel: '상담 안내',
    columns: [
      {
        title: '상담',
        icon: 'mdi-headset',
        links: [
          { label: '상담 신청 내역', to: '/consult-board', desc: '신청 내용과 답변을 확인합니다' },
          { label: '찾아오시는 길', to: '/support', desc: '연락처 · 위치' },
        ],
      },
    ],
    feature: {
      badge: '상담 신청',
      title: '온라인 상담 신청',
      desc: '임금대장 1개월분을 첨부하면 기관의 임금 구조를 함께 살펴봅니다.',
      cta: '상담 신청',
      to: '/consult-board/write',
      icon: 'mdi-clipboard-edit-outline',
    },
  },
]

/** 홈 히어로 — 기관 유형 선택 */
export interface PortalOrgChoice {
  id: string
  label: string
  desc: string
  to: string
  icon: string
  cta: string
  image: string
}

export const PORTAL_ORG_CHOICES: PortalOrgChoice[] = [
  {
    id: 'facility',
    label: ORG_TYPE_FACILITY,
    desc: '인건비 · 통상임금',
    to: '/facility',
    icon: 'mdi-home-heart',
    cta: '바로 보기',
    image: images.orgFacility,
  },
  {
    id: 'visit',
    label: ORG_TYPE_VISIT,
    desc: '청구 · 급여 · 신고',
    to: '/visiting-care',
    icon: 'mdi-map-marker-radius',
    cta: '바로 보기',
    image: images.orgVisitGate,
  },
]

/** 홈 · 검색 추천 — 핵심 4축 */
export const PORTAL_HERO_CORE: PortalShortcut[] = [
  { label: '임금관리', icon: 'mdi-cash-multiple', to: '/wage', desc: '설계 · 대장 · 신고' },
  { label: '회계관리', icon: 'mdi-calculator-variant-outline', to: '/accounting', desc: '장부 · 예결산' },
  { label: '고시 검색', icon: 'mdi-file-search-outline', to: NOTICE_PUBLIC_PATH, desc: '법령 · 고시' },
  { label: '함우리몰', icon: 'mdi-cart-percent', to: '/mall', desc: '기관 공동구매' },
]

export const PORTAL_PILLARS = PORTAL_HERO_CORE

/** 자주 찾는 서비스 (대시보드 빠른 실행) */
export const PORTAL_FREQ_SERVICES: PortalShortcut[] = [
  { key: 'wage', label: '임금관리', icon: 'mdi-cash-multiple', to: '/wage', desc: '급여 · 명세서 · 신고' },
  { key: 'accounting', label: '재무회계', icon: 'mdi-calculator-variant-outline', to: '/accounting', desc: '세무 · 장부 · 결산' },
  { key: 'gosi', label: '고시 검색', icon: 'mdi-file-search-outline', to: NOTICE_PUBLIC_PATH, desc: '법령 · 고시' },
  { key: 'group-purchase', label: '함우리몰', icon: 'mdi-cart-percent', to: '/mall', desc: '기관 공동구매' },
  { key: 'consult-write', label: '상담 신청', icon: 'mdi-clipboard-check-outline', to: '/consult-board/write', desc: '임금 · 회계 · 기관 운영' },
]

export interface PortalServiceGroup {
  title: string
  icon: string
  desc?: string
  items: PortalShortcut[]
}

/** 주요 서비스 — 기관유형별 그룹 */
export const PORTAL_SERVICE_GROUPS: PortalServiceGroup[] = [
  {
    title: '임금관리 · 기관유형',
    icon: 'mdi-cash-multiple',
    desc: '임금관리는 기관 유형에 따라 전용 페이지로 연결됩니다.',
    items: [
      { key: 'wage-facility', label: '요양시설 임금관리', icon: 'mdi-home-heart', to: '/facility', desc: '인건비 · 통상임금 임금관리' },
      { key: 'wage-visit', label: '방문센터 임금관리', icon: 'mdi-map-marker-radius', to: '/visiting-care', desc: '청구 · 급여 연계 임금관리' },
    ],
  },
  {
    title: '공통 서비스',
    icon: 'mdi-apps',
    desc: '재무회계 · 고시 · 공동구매는 모든 기관이 함께 이용합니다.',
    items: [
      { key: 'accounting', label: '재무회계', icon: 'mdi-calculator-variant-outline', to: '/accounting', desc: '장부 · 예결산 · 감사' },
      { key: 'gosi', label: '고시 검색', icon: 'mdi-file-search-outline', to: NOTICE_PUBLIC_PATH, desc: '법령 · 고시 검색' },
      { key: 'group-purchase', label: '함우리몰', icon: 'mdi-cart-percent', to: '/mall', desc: '기관 공동구매' },
    ],
  },
]

/** 즐겨찾기 키 계산 (key 우선, 없으면 to) */
export function getFavoriteKey(item: PortalShortcut): string {
  return item.key ?? item.to
}

/** 즐겨찾기 대상 전체 (키 → 항목 해석용) */
export const PORTAL_FAVORITE_ITEMS: PortalShortcut[] = [
  ...PORTAL_SERVICE_GROUPS.flatMap((group) => group.items),
  ...PORTAL_FREQ_SERVICES,
]

export function resolveFavorite(key: string): PortalShortcut | undefined {
  return PORTAL_FAVORITE_ITEMS.find((item) => getFavoriteKey(item) === key)
}

/** 기관 유형별 안내 (정부24 생활안내 대응) */
export const PORTAL_ORG_GUIDE: PortalOrgGuide[] = [
  {
    id: 'facility',
    label: ORG_TYPE_FACILITY,
    icon: 'mdi-home-heart',
    desc: '인건비 · 통상임금',
    links: [
      { label: '요양시설 임금관리', to: '/facility' },
      { label: '임금 서비스', to: '/wage' },
      { label: '회계 서비스', to: '/accounting' },
    ],
  },
  {
    id: 'visit',
    label: ORG_TYPE_VISIT,
    icon: 'mdi-map-marker-radius',
    desc: '청구 · 급여',
    links: [
      { label: '방문센터 임금관리', to: '/visiting-care' },
      { label: '급여 계산', to: '/visiting-care' },
      { label: '상담 신청', to: '/consult-board/write' },
    ],
  },
  {
    id: 'buy',
    label: '함우리몰',
    icon: 'mdi-cart-percent',
    desc: '기관 공동구매',
    links: [
      { label: '쇼핑몰 가기', to: '/mall' },
      { label: '마이쇼핑', to: '/mall/mypage' },
    ],
  },
]

/** 원스톱 서비스 탭 */
export const PORTAL_ONE_STOP_TABS: PortalOneStopTab[] = [
  {
    id: 'all',
    label: '전체',
    links: [
      { label: '임금 구조 점검', to: '/wage', icon: 'mdi-cash-multiple' },
      { label: '회계 · 예결산', to: '/accounting', icon: 'mdi-calculator-variant-outline' },
      { label: '고시 검색', to: NOTICE_PUBLIC_PATH, icon: 'mdi-file-search-outline' },
      { label: '함우리몰', to: '/mall', icon: 'mdi-cart-outline' },
      { label: '상담 신청', to: '/consult-board/write', icon: 'mdi-forum-outline' },
      { label: 'AI 업무 지원', to: NOTICE_PUBLIC_PATH, icon: 'mdi-robot-outline' },
    ],
  },
  {
    id: 'wage',
    label: '임금관리',
    links: [
      { label: '임금 서비스', to: '/wage', icon: 'mdi-cash-multiple' },
      { label: '상담 신청', to: '/consult-board/write', icon: 'mdi-clipboard-check-outline' },
      { label: '통상임금 점검', to: '/wage', icon: 'mdi-shield-check-outline' },
    ],
  },
  {
    id: 'accounting',
    label: '재무회계',
    links: [
      { label: '회계 서비스', to: '/accounting', icon: 'mdi-calculator-variant-outline' },
      { label: '예 · 결산', to: '/accounting', icon: 'mdi-book-open-variant' },
      { label: '감사 대비', to: '/accounting', icon: 'mdi-file-document-check-outline' },
    ],
  },
  {
    id: 'gosi',
    label: '고시',
    links: [
      { label: '고시 검색', to: NOTICE_PUBLIC_PATH, icon: 'mdi-file-search-outline' },
      { label: '공지사항', to: NOTICE_PUBLIC_PATH, icon: 'mdi-bullhorn-outline' },
      { label: '체크리스트', to: NOTICE_PUBLIC_PATH, icon: 'mdi-checkbox-marked-outline' },
    ],
  },
  {
    id: 'buy',
    label: '함우리몰',
    links: [
      { label: '쇼핑몰 가기', to: '/mall', icon: 'mdi-cart-outline' },
      { label: '마이쇼핑', to: '/mall/mypage', icon: 'mdi-account-outline' },
    ],
  },
]

/** 혜택·서비스 알림 (정부24 혜택알림 대응) */
export const PORTAL_BENEFITS: PortalBenefit[] = [
  {
    title: '통상임금 무료 진단',
    desc: '2024.12 판결 이후 임금 구조를 점검합니다. 임금대장 1개월분이면 시작할 수 있습니다.',
    to: '/consult-board/write',
  },
  {
    title: '카이온 이용 시 함우리몰 추가 할인',
    desc: '임금 · 회계 서비스 이용 기관은 공동구매 참여 시 추가 할인이 적용됩니다.',
    to: '/mall',
  },
  {
    title: '고시 개정 알림',
    desc: '장기요양급여 고시 변경사항을 기관 유형에 맞게 안내합니다.',
    to: NOTICE_PUBLIC_PATH,
  },
]

/** 로그인 박스 하단 MY 링크 */
export const PORTAL_AUTH_LINKS: PortalAuthLink[] = [
  { label: '마이페이지', icon: 'mdi-account-circle-outline', to: '/my-info' },
  { label: '함우리몰', icon: 'mdi-cart-outline', to: '/mall' },
  { label: '고시 검색', icon: 'mdi-file-search-outline', to: NOTICE_PUBLIC_PATH },
]

/** 배너 슬라이드 */
export const PORTAL_PROMO_SLIDES: PortalPromoSlide[] = [
  {
    title: '임금관리 — 인건비 절감과 통상임금 리스크 점검',
    desc: '수당 · 비과세 설계부터 4대보험 · 세무 신고까지',
    cta: '임금 서비스 보기',
    to: '/wage',
    tone: 'wage',
  },
  {
    title: '회계관리 — 장기요양기관 회계규칙 준수',
    desc: '장부 · 예결산 · 감사 대비를 하나의 흐름으로',
    cta: '회계 서비스 보기',
    to: '/accounting',
    tone: 'accounting',
  },
  {
    title: '함우리몰 — 함께 모이면 운영비는 가벼워집니다',
    desc: '전국 1,800+ 기관이 참여하는 기관 공동구매',
    cta: '함우리몰 입장',
    to: '/mall',
    tone: 'buy',
  },
]

/** 인기 검색어 */
export const PORTAL_POPULAR_SEARCHES: string[] = [
  '통상임금',
  '임금대장',
  '공동구매',
  '고시 개정',
  '회계 예결산',
  '4대보험',
]

/** AI 빠른 질문 추천 */
export const PORTAL_AI_PROMPTS: string[] = [
  '통상임금 계산 방법 알려줘',
  '이번 달 공동구매 추천해줘',
  '회계 결산 일정이 어떻게 돼?',
  '최근 고시 개정 내용 요약해줘',
]

export interface PortalTask {
  id: string
  title: string
  due: string
  status: 'urgent' | 'todo' | 'done'
  to: string
  icon: string
}

/** 오늘 처리할 업무 (대시보드) */
export const PORTAL_DASHBOARD_TASKS: PortalTask[] = [
  {
    id: 't1',
    title: '6월 임금대장 검토 및 신고 마감',
    due: '오늘 마감',
    status: 'urgent',
    to: '/wage',
    icon: 'mdi-cash-multiple',
  },
  {
    id: 't2',
    title: '통상임금 무료 진단 결과 확인',
    due: 'D-2',
    status: 'todo',
    to: '/consult-board/write',
    icon: 'mdi-clipboard-check-outline',
  },
  {
    id: 't3',
    title: '2분기 회계 예결산 자료 정리',
    due: 'D-5',
    status: 'todo',
    to: '/accounting',
    icon: 'mdi-calculator-variant-outline',
  },
  {
    id: 't4',
    title: '위생용품 공동구매 참여 신청',
    due: '완료',
    status: 'done',
    to: '/mall',
    icon: 'mdi-cart-check',
  },
]

/** 하단 접이식 링크 */
export const PORTAL_FOOTER_EXPAND = [
  {
    title: '관련 서비스',
    links: [
      { label: '요양시설 임금관리', to: '/facility' },
      { label: '방문센터 임금관리', to: '/visiting-care' },
      { label: '함우리몰', to: '/mall' },
    ],
  },
  {
    title: '정책·자료',
    links: [
      { label: '고시 검색', to: NOTICE_PUBLIC_PATH },
      { label: '공지사항', to: NOTICE_PUBLIC_PATH },
      { label: '자료실', to: NOTICE_PUBLIC_PATH },
    ],
  },
  {
    title: '상담',
    links: [
      { label: '상담 신청', to: '/consult-board/write' },
      { label: '회원가입', to: '/signup' },
    ],
  },
]
